import { AfterViewInit, Component, ElementRef, Injector, OnDestroy, effect, inject, viewChild } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";

import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

// NOTE: adjust this import path to wherever these interfaces actually live in the app.
import { ActiveRound, CellState, Tile, TileVisualState } from "@models";
import { ActionService } from "@services";

export type TileAsset = "gold" | "dynamite";

const COLS = 5;
const ROWS = 5;
const MAX_TILE_W = 64;
const MAX_TILE_H = 62;
const GAP = 10;
const FLIP_DURATION_MS = 220;

// Fixed padding around the grid, inside the host element.
const PADDING_TOP = 16;
const PADDING_LEFT = 16;
const PADDING_RIGHT = 16;

// Waiting-pick "shine" sweep (top-left -> bottom-right). Plays once per trigger.
const SHINE_DURATION_MS = 500;

// How long to leave the crash reveal (mines shown) on screen before resetting the board.
const CRASH_RESET_DELAY_MS = 800;

// Reset "drop" animation: tiles fall away column by column (left -> right), and within
// each column, tile by tile from top to bottom, like a cascading waterfall.
const DROP_DURATION_MS = 250;
const DROP_COLUMN_STAGGER_MS = 50;
const DROP_ROW_STAGGER_MS = 60;
// How many tile-heights each tile travels while dropping — needs to be several, not
// a fraction of one, or the fall reads as a flicker instead of a fall.
const DROP_FALL_HEIGHTS = 2.6;
// Fraction of the drop's progress where fade-out begins — tiles stay fully opaque
// while actually falling, and only dissolve right at the end, out of view.
const DROP_FADE_START = 0.65;

@UntilDestroy()
@Component({
  selector: "mn-game-animations",
  templateUrl: "./game-animations.component.html",
  styleUrls: ["./game-animations.component.scss"],
})
export class GameAnimationsComponent implements AfterViewInit, OnDestroy {
  private readonly acS = inject(ActionService);
  private readonly eR = inject(ElementRef);
  private readonly inj = inject(Injector);

  public canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("canvas");

  private readonly assetPaths: { gold: string; dynamite: string } = {
    gold: "assets/images/webp/game/gold.webp",
    dynamite: "assets/images/webp/game/dynamite.webp",
  };

  private ctx!: CanvasRenderingContext2D;
  private tiles: Tile[] = [];
  private resizeObserver!: ResizeObserver;
  private dpr = window.devicePixelRatio || 1;

  private cssWidth = 0;
  private cssHeight = 0;
  private tileW = MAX_TILE_W;
  private tileH = MAX_TILE_H;
  private offsetX = 0;
  private offsetY = 0;

  private hoveredIndex: number | null = null;
  private pressedIndex: number | null = null;
  private activePointerId: number | null = null;

  private images: Partial<Record<TileAsset, HTMLImageElement>> = {};
  private rafId: number | null = null;
  private needsRedraw = true;

  // --- waiting-pick shine state (single pass) ---
  private waitingPick = false;
  private waitingPickStart = 0;

  // --- last seen round selection, to diff for "newly picked" cells ---
  private lastSelectedCells: number[] = [];

  // --- pending reset after a crash reveal ---
  private crashResetTimer: ReturnType<typeof setTimeout> | null = null;

  // --- generic "an animation is in flight" counter, mirrored to acS.aP$. Covers both
  //     reveal-flip animations AND resetAll's per-tile drop animation. ---
  private animationCount = 0;

  // --- true from the moment a crash is detected until the board has fully reset;
  //     blocks all tile input while the crash reveal / drop is happening. ---
  private crashed = false;

  // --- an extra animationCount credit held from the moment of crash until the drop that
  //     follows it fully completes, so `aP$` doesn't dip false in the gap between the mine
  //     reveal finishing and the drop starting (during CRASH_RESET_DELAY_MS). ---
  private crashHoldOutstanding = false;

  // --- resetAll's staggered per-tile drop animation ---
  private readonly dropStartTimes = new Map<number, number>();
  private readonly dropFinalizeTimers = new Map<number, ReturnType<typeof setTimeout>>();

  constructor() {
    effect(() => {
      if (!this.acS.wP$()) {
        this.stopWaitingPick();
        return;
      }

      this.onWaitingPick();
    });
  }

  public ngAfterViewInit(): void {
    this.buildTiles();
    this.preloadAssets();

    const canvas = this.canvasRef()!.nativeElement;
    this.ctx = canvas.getContext("2d")!;
    // Pointer events drive everything; stop the browser from turning
    // touch drags/holds into scroll/zoom gestures on the canvas.
    canvas.style.touchAction = "none";

    this.attachEvents(canvas);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.eR.nativeElement);
    this.handleResize();

    toObservable(this.acS.aR$, { injector: this.inj })
      .pipe(untilDestroyed(this))
      .subscribe(r => this.onActiveRoundChange(r));

    const loop = (): void => {
      // Keep redrawing every frame while the shine sweep, a loading pulse,
      // or the reset drop animation is running, even if nothing else changed.
      if (this.needsRedraw || this.waitingPick || this.hasLoadingTile() || this.dropStartTimes.size > 0) {
        this.draw();
      }
      this.rafId = requestAnimationFrame(loop);
    };

    this.rafId = requestAnimationFrame(loop);
  }

  public ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    if (this.crashResetTimer !== null) {
      clearTimeout(this.crashResetTimer);
    }

    this.dropFinalizeTimers.forEach(timer => clearTimeout(timer));
    this.dropFinalizeTimers.clear();

    this.resizeObserver?.disconnect();
  }

  // ---------------------------------------------------------------------
  // Public API — call these once the BE responds with an outcome for a tile
  // ---------------------------------------------------------------------

  /** Flip a tile face-down->face-up revealing gold or dynamite. */
  private revealTile(index: number, asset: TileAsset): void {
    const tile = this.tiles.find(t => t.index === index);
    if (!tile || tile.state === "revealed" || tile.state === "flipping") return;

    tile.asset = asset;
    tile.state = "flipping";
    tile.flipPhase = "out";

    this.beginAnimation();
    this.animateFlip(tile);
  }

  /** Reset the whole board — tiles drop away column by column, tile by tile from top to
   *  bottom within each column, then settle idle. Counted as an in-flight animation, so
   *  `acS.aP$` stays true for the whole cascade. */
  private resetAll(): void {
    const now = performance.now();

    // A previous drop is only "interrupted" (as opposed to this being the drop that a
    // just-detected crash itself scheduled) if one was still mid-flight when we got here.
    const wasDropInFlight = this.dropStartTimes.size > 0;

    // Cancel any drop animation already in flight before starting a new one, making sure
    // the animation counter stays balanced for every tile whose drop we're cutting short.
    this.dropStartTimes.forEach(() => this.endAnimation());
    this.dropFinalizeTimers.forEach(timer => clearTimeout(timer));
    this.dropFinalizeTimers.clear();
    this.dropStartTimes.clear();

    // An interrupted drop means whatever cycle it belonged to (e.g. an earlier crash) is
    // being abandoned — release its held animation credit now, since it'll never reach
    // its own completion callback below.
    if (wasDropInFlight && this.crashHoldOutstanding) {
      this.crashHoldOutstanding = false;
      this.endAnimation();
    }

    // Capture whether *this* reset was triggered by a crash before clearing the flag, so
    // the drop-completion callback below knows whether it's the one that owns the hold.
    const wasCrash = this.crashed;
    let pendingDrops = this.tiles.length;

    this.tiles.forEach(t => {
      const delay = t.col * DROP_COLUMN_STAGGER_MS + t.row * DROP_ROW_STAGGER_MS;
      this.dropStartTimes.set(t.index, now + delay);
      this.beginAnimation();

      const timer = setTimeout(() => {
        t.state = "idle";
        t.asset = undefined;
        t.flipScaleX = 1;
        t.flipPhase = "none";
        this.dropStartTimes.delete(t.index);
        this.dropFinalizeTimers.delete(t.index);
        this.needsRedraw = true;
        this.syncCells();
        this.endAnimation();

        pendingDrops--;
        if (pendingDrops === 0 && wasCrash && this.crashHoldOutstanding) {
          // Every tile has finished dropping — the crash-to-reset animation is fully done.
          this.crashHoldOutstanding = false;
          this.endAnimation();
        }
      }, delay + DROP_DURATION_MS);

      this.dropFinalizeTimers.set(t.index, timer);
    });

    this.needsRedraw = true;
    this.crashed = false;

    this.acS.aR$.set(null);
    this.acS.wP$.set(false);
  }

  // ---------------------------------------------------------------------
  // Round reactions
  // ---------------------------------------------------------------------

  /** Waiting for the player to pick a cell — sweep a diagonal shine over the tiles, once. */
  private onWaitingPick(): void {
    if (this.waitingPick) return; // already sweeping, let it finish
    this.waitingPick = true;
    this.waitingPickStart = performance.now();
    this.needsRedraw = true;
  }

  private stopWaitingPick(): void {
    if (!this.waitingPick) return;
    this.waitingPick = false;
    this.needsRedraw = true;
  }

  /** Mark an animation (reveal flip or drop) as started; flips `acS.aP$` true on the first one in flight. */
  private beginAnimation(): void {
    if (this.animationCount === 0) {
      this.acS.aP$.set(true);
    }
    this.animationCount++;
  }

  /** Mark an animation (reveal flip or drop) as finished; flips `acS.aP$` false once none remain. */
  private endAnimation(): void {
    this.animationCount = Math.max(0, this.animationCount - 1);
    if (this.animationCount === 0) {
      this.acS.aP$.set(false);
    }
  }

  /**
   * React to the active round's state.
   * - Round finished, or `revealed_safe` no longer matches `selected_cells`
   *   (i.e. a pick wasn't safe) -> crash: lock the board, reveal every mine from
   *   `mine_positions`, then reset the whole board back to idle after a short delay.
   * - Otherwise (`revealed_safe` === `selected_cells`) -> the newest cell(s)
   *   in `selected_cells` were safe picks: flip them to show gold.
   */
  private onActiveRoundChange(round: ActiveRound | null | undefined): void {
    if (!round?.data) {
      return;
    }

    const { revealed_safe: revealedSafe = [], selected_cells: selectedCells = [], mine_positions: minePositions = [] } = round.data;

    // Fresh/empty round (e.g. just started, nothing picked yet) — make sure
    // the board isn't left showing a previous round's state.
    if (revealedSafe.length === 0 && selectedCells.length === 0) {
      if (this.crashResetTimer !== null) {
        clearTimeout(this.crashResetTimer);
        this.crashResetTimer = null;
      }
      this.resetAll();
      this.lastSelectedCells = [];
      return;
    }

    const isSafeSoFar = this.sameCells(revealedSafe, selectedCells);

    if (round.finished || !isSafeSoFar) {
      if (!this.crashed) {
        this.crashed = true;
        this.crashHoldOutstanding = true;
        this.beginAnimation();
      }

      minePositions.forEach(pos => this.revealTile(this.cellToTileIndex(pos), "dynamite"));
      this.lastSelectedCells = selectedCells;

      if (this.crashResetTimer !== null) {
        clearTimeout(this.crashResetTimer);
      }
      this.crashResetTimer = setTimeout(() => {
        this.crashResetTimer = null;
        this.resetAll();
        this.lastSelectedCells = [];
      }, CRASH_RESET_DELAY_MS);
      return;
    }

    const newlyPicked = selectedCells.filter(cell => !this.lastSelectedCells.includes(cell));
    newlyPicked.forEach(cell => this.revealTile(this.cellToTileIndex(cell), "gold"));

    this.lastSelectedCells = selectedCells;
  }

  /** Order-independent equality check for two cell-index arrays. */
  private sameCells(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    const setA = new Set(a);
    return b.every(v => setA.has(v));
  }

  /**
   * Maps a BE cell index to our tile index. `selected_cells`, `revealed_safe`
   * and `mine_positions` are 1-based (1..25), same as `Tile.index`, so this is
   * currently an identity mapping — kept as a named seam in case that changes.
   */
  private cellToTileIndex(cell: number): number {
    return cell;
  }

  /** Push current per-cell state to the shared store, e.g. for "pick a random unrevealed cell". */
  private syncCells(): void {
    const cells: CellState[] = this.tiles.map(t => ({ index: t.index, state: t.state }));
    this.acS.cells$.set(cells);
  }

  private hasLoadingTile(): boolean {
    return this.tiles.some(t => t.state === "loading");
  }

  // ---------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------

  private buildTiles(): void {
    this.tiles = [];
    let index = 1;
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        this.tiles.push({
          index,
          row,
          col,
          x: 0,
          y: 0,
          w: MAX_TILE_W,
          h: MAX_TILE_H,
          state: "idle",
          flipScaleX: 1,
          flipPhase: "none",
        });
        index++;
      }
    }
    this.syncCells();
  }

  private preloadAssets(): void {
    (["gold", "dynamite"] as TileAsset[]).forEach(key => {
      const img = new Image();
      img.src = this.assetPaths[key];
      img.onload = () => (this.needsRedraw = true);
      this.images[key] = img;
    });
  }

  // ---------------------------------------------------------------------
  // Sizing / responsiveness
  // ---------------------------------------------------------------------

  private handleResize(): void {
    const host = this.eR.nativeElement;

    // Reserve fixed padding on the left/right/top; the grid is sized and
    // centered within whatever space remains.
    const availW = host.clientWidth - PADDING_LEFT - PADDING_RIGHT;
    const availH = (host.clientHeight || host.clientWidth) - PADDING_TOP;

    const scaleW = (availW - GAP * (COLS - 1)) / COLS / MAX_TILE_W;
    const scaleH = (availH - GAP * (ROWS - 1)) / ROWS / MAX_TILE_H;
    const scale = Math.max(0.1, Math.min(1, scaleW, scaleH));

    this.tileW = MAX_TILE_W * scale;
    this.tileH = MAX_TILE_H * scale;
    const gap = GAP * scale;

    this.cssWidth = this.tileW * COLS + gap * (COLS - 1);
    this.cssHeight = this.tileH * ROWS + gap * (ROWS - 1);

    // Center the grid within the padded area, so PADDING_LEFT/PADDING_RIGHT
    // and PADDING_TOP are guaranteed as a minimum, not just a target.
    this.offsetX = PADDING_LEFT + (availW - this.cssWidth) / 2;
    this.offsetY = PADDING_TOP + (availH - this.cssHeight) / 2;

    this.tiles.forEach(t => {
      t.w = this.tileW;
      t.h = this.tileH;
      t.x = this.offsetX + t.col * (this.tileW + gap);
      t.y = this.offsetY + t.row * (this.tileH + gap);
    });

    const canvas = this.canvasRef()!.nativeElement;

    this.dpr = window.devicePixelRatio || 1;
    canvas.width = host.clientWidth * this.dpr;
    canvas.height = (host.clientHeight || host.clientWidth) * this.dpr;
    canvas.style.width = `${host.clientWidth}px`;
    canvas.style.height = `${host.clientHeight || host.clientWidth}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.needsRedraw = true;
  }

  // ---------------------------------------------------------------------
  // Input handling (pointer events — unified mouse/touch/pen, mobile-first)
  // ---------------------------------------------------------------------

  private attachEvents(canvas: HTMLCanvasElement): void {
    canvas.addEventListener("pointermove", e => this.onPointerMove(e));
    canvas.addEventListener("pointerdown", e => this.onPointerDown(e));
    canvas.addEventListener("pointerup", e => this.onPointerUp(e));
    canvas.addEventListener("pointercancel", () => this.onPointerCancel());
    canvas.addEventListener("pointerleave", e => this.onPointerLeave(e));
  }

  private getLocalPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvasRef()!.nativeElement.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  private hitTest(x: number, y: number): Tile | null {
    for (const t of this.tiles) {
      if (x >= t.x && x <= t.x + t.w && y >= t.y && y <= t.y + t.h) return t;
    }
    return null;
  }

  /** A tile is interactive only if it's idle/hover, nothing on the board is currently
   *  loading, no reveal-flip or drop animation is in flight anywhere, and the round
   *  hasn't just crashed. One pick must fully resolve (and any crash must finish
   *  resetting) before another pick can start. */
  private isInteractive(t: Tile): boolean {
    return (t.state === "idle" || t.state === "hover") && !this.hasLoadingTile() && !this.crashed && this.animationCount === 0;
  }

  private onPointerMove(e: PointerEvent): void {
    // Hover is a mouse/pen affordance only — touch drags shouldn't leave
    // tiles stuck in a "hover" state.
    if (e.pointerType === "touch") return;

    const { x, y } = this.getLocalPoint(e.clientX, e.clientY);
    const tile = this.hitTest(x, y);
    const newHover = tile && this.isInteractive(tile) ? tile.index : null;

    if (newHover !== this.hoveredIndex) {
      if (this.hoveredIndex !== null) this.setStateIfInteractive(this.hoveredIndex, "idle");
      if (newHover !== null) this.setStateIfInteractive(newHover, "hover");
      this.hoveredIndex = newHover;
      this.needsRedraw = true;
    }
  }

  private onPointerDown(e: PointerEvent): void {
    const { x, y } = this.getLocalPoint(e.clientX, e.clientY);
    const tile = this.hitTest(x, y);
    if (tile && this.isInteractive(tile)) {
      tile.state = "pressed";
      this.pressedIndex = tile.index;
      this.activePointerId = e.pointerId;
      // Keep receiving pointerup/pointermove even if the finger drifts off canvas.
      this.canvasRef()!.nativeElement.setPointerCapture(e.pointerId);
      this.needsRedraw = true;
    }
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.pressedIndex === null || e.pointerId !== this.activePointerId) return;
    const tile = this.tiles.find(t => t.index === this.pressedIndex);
    this.pressedIndex = null;
    this.activePointerId = null;
    if (tile && tile.state === "pressed") {
      this.emitTileClick(tile);
    }
  }

  private onPointerCancel(): void {
    this.clearPress();
  }

  private onPointerLeave(e: PointerEvent): void {
    if (e.pointerType !== "touch" && this.hoveredIndex !== null) {
      this.setStateIfInteractive(this.hoveredIndex, "idle");
      this.hoveredIndex = null;
      this.needsRedraw = true;
    }
    // A captured pointer won't fire "leave" on release, but a cancel or an
    // uncaptured stray leave should still drop any stuck press state.
    if (this.activePointerId === null) {
      this.clearPress();
    }
  }

  private clearPress(): void {
    if (this.pressedIndex === null) return;
    this.setStateIfInteractive(this.pressedIndex, "idle");
    this.pressedIndex = null;
    this.activePointerId = null;
    this.needsRedraw = true;
  }

  /** Click/tap a tile: move it to `loading` immediately (optimistic UI) until
   *  `onActiveRoundChange` confirms the outcome and flips it to gold/dynamite.
   *  While a tile is `loading`, `isInteractive()` blocks every other tile. */
  private emitTileClick(tile: Tile): void {
    console.log("[tile-panel] tile clicked", tile);

    tile.state = "loading";

    this.needsRedraw = true;

    this.syncCells();

    this.acS.tE$.next({ index: tile.index, state: tile.state });
  }

  private setStateIfInteractive(index: number, state: TileVisualState): void {
    const tile = this.tiles.find(t => t.index === index);
    if (tile && (tile.state === "idle" || tile.state === "hover")) {
      tile.state = state;
    }
  }

  // ---------------------------------------------------------------------
  // Flip animation
  // ---------------------------------------------------------------------

  private animateFlip(tile: Tile): void {
    const start = performance.now();
    const step = (now: number): void => {
      const elapsed = now - start;
      const half = FLIP_DURATION_MS;

      if (tile.flipPhase === "out") {
        const t = Math.min(1, elapsed / half);
        tile.flipScaleX = 1 - t;
        this.needsRedraw = true;
        if (t >= 1) {
          tile.flipPhase = "in";
          this.animateFlip(tile); // restart timer for the "in" half
          return;
        }
      } else if (tile.flipPhase === "in") {
        const t = Math.min(1, elapsed / half);
        tile.flipScaleX = t;
        this.needsRedraw = true;
        if (t >= 1) {
          tile.flipScaleX = 1;
          tile.flipPhase = "none";
          tile.state = "revealed";
          this.needsRedraw = true;
          this.syncCells();
          this.endAnimation();
          return;
        }
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // ---------------------------------------------------------------------
  // Drawing
  // ---------------------------------------------------------------------

  private draw(): void {
    this.needsRedraw = false;
    const ctx = this.ctx;
    const canvas = this.canvasRef()!.nativeElement;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const tile of this.tiles) {
      this.drawTile(ctx, tile);
    }

    if (this.waitingPick) {
      this.drawWaitingShine(ctx);
    }
  }

  /**
   * Diagonal light sweep, top-left -> bottom-right, clipped to each individual
   * face-down tile (so gaps between tiles stay dark). Plays once and stops.
   */
  private drawWaitingShine(ctx: CanvasRenderingContext2D): void {
    if (this.cssWidth <= 0 || this.cssHeight <= 0) return;

    const elapsed = performance.now() - this.waitingPickStart;
    if (elapsed >= SHINE_DURATION_MS) {
      this.stopWaitingPick();
      return;
    }
    const progress = elapsed / SHINE_DURATION_MS; // 0 -> 1, single pass

    const diagonal = Math.hypot(this.cssWidth, this.cssHeight);
    const angle = Math.atan2(this.cssHeight, this.cssWidth);
    const bandWidth = Math.max(24, this.cssWidth * 0.22);
    const travel = diagonal + bandWidth * 2;
    // local x position of the band's center, from just-off-start to just-off-end
    const bandX = -diagonal / 2 - bandWidth + progress * travel;

    const gridCenterX = this.offsetX + this.cssWidth / 2;
    const gridCenterY = this.offsetY + this.cssHeight / 2;

    for (const tile of this.tiles) {
      // Shine only makes sense on face-down tiles.
      if (tile.state === "revealed" || tile.state === "flipping") continue;

      const radius = Math.min(14, tile.w * 0.2);

      ctx.save();
      this.roundRect(ctx, tile.x, tile.y, tile.w, tile.h, radius);
      ctx.clip();

      ctx.translate(gridCenterX, gridCenterY);
      ctx.rotate(angle);

      const grad = ctx.createLinearGradient(bandX - bandWidth / 2, 0, bandX + bandWidth / 2, 0);
      grad.addColorStop(0, "rgba(255,255,255,0)");
      grad.addColorStop(0.5, "rgba(255,255,255,0.35)");
      grad.addColorStop(1, "rgba(255,255,255,0)");

      ctx.fillStyle = grad;
      ctx.fillRect(bandX - bandWidth / 2, -diagonal, bandWidth, diagonal * 2);

      ctx.restore();
    }
  }

  private drawTile(ctx: CanvasRenderingContext2D, tile: Tile): void {
    // --- reset "drop" animation: tile visibly falls several tile-heights, picking up
    //     speed like gravity, with a slight tumble, and only fades out right at the end
    //     (once it's actually fallen out of view) rather than dissolving in place. ---
    let dropOffsetY = 0;
    let dropAlpha = 1;
    let dropRotation = 0;
    let dropScale = 1;
    const dropStart = this.dropStartTimes.get(tile.index);
    if (dropStart !== undefined) {
      const elapsed = performance.now() - dropStart;
      if (elapsed > 0) {
        const progress = Math.min(1, elapsed / DROP_DURATION_MS);
        const eased = progress * progress; // ease-in, feels like gravity accelerating

        const fallDistance = tile.h * DROP_FALL_HEIGHTS + GAP;
        dropOffsetY = eased * fallDistance;

        dropAlpha = progress < DROP_FADE_START ? 1 : 1 - (progress - DROP_FADE_START) / (1 - DROP_FADE_START);

        // Subtle shrink + alternating tilt per column sells a bit of "falling away" depth.
        dropScale = 1 - progress * 0.12;
        dropRotation = (tile.col % 2 === 0 ? 1 : -1) * progress * 0.12;
      }
    }

    const cx = tile.x + tile.w / 2;
    const cy = tile.y + tile.h / 2;

    ctx.save();
    ctx.globalAlpha = dropAlpha;
    ctx.translate(cx, cy + dropOffsetY);
    ctx.rotate(dropRotation);
    ctx.scale(dropScale, dropScale);
    ctx.translate(-cx, -cy);

    this.drawTileFace(ctx, tile);

    ctx.restore();
  }

  private drawTileFace(ctx: CanvasRenderingContext2D, tile: Tile): void {
    const bezel = Math.max(2, tile.h * 0.07);
    const radius = Math.min(14, tile.w * 0.2);

    const isPressed = tile.state === "pressed";
    const isHover = tile.state === "hover";
    const isLoading = tile.state === "loading";
    const isRevealed = tile.state === "revealed";
    const isFlipping = tile.state === "flipping";

    // Flip squash: scale horizontally around tile center.
    const scaleX = isFlipping ? Math.max(0.02, Math.abs(tile.flipScaleX)) : 1;
    const cx = tile.x + tile.w / 2;
    const cy = tile.y + tile.h / 2;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(scaleX, 1);
    ctx.translate(-cx, -cy);

    // --- bottom bezel (3D depth) ---
    const bezelHeight = isPressed ? 0 : bezel;
    this.roundRect(ctx, tile.x, tile.y + bezelHeight, tile.w, tile.h - bezelHeight, radius);
    ctx.fillStyle = "#131a29";
    ctx.fill();

    // --- top face ---
    const topY = isPressed ? tile.y + bezel : tile.y;
    const topH = tile.h - bezel;
    this.roundRect(ctx, tile.x, topY, tile.w, topH, radius);

    const grad = ctx.createLinearGradient(0, topY, 0, topY + topH);
    if (isRevealed || isFlipping) {
      grad.addColorStop(0, "#3a4a68");
      grad.addColorStop(1, "#1e2637");
    } else if (isLoading) {
      // Darker than idle — the tile is "occupied" while we wait on the BE outcome.
      grad.addColorStop(0, "#141b2c");
      grad.addColorStop(1, "#0a0f1a");
    } else if (isHover) {
      grad.addColorStop(0, "#4a5b80");
      grad.addColorStop(1, "#28324a");
    } else if (isPressed) {
      grad.addColorStop(0, "#2a3348");
      grad.addColorStop(1, "#1a2133");
    } else {
      grad.addColorStop(0, "#3d4a68");
      grad.addColorStop(1, "#232c40");
    }
    ctx.fillStyle = grad;
    ctx.fill();

    // subtle top highlight border
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(tile.x + radius, topY + 1);
    ctx.lineTo(tile.x + tile.w - radius, topY + 1);
    ctx.stroke();
    ctx.restore();

    if (isHover) {
      ctx.save();
      this.roundRect(ctx, tile.x, topY, tile.w, topH, radius);
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }

    if (isLoading) {
      this.drawLoader(ctx, tile, topY, topH);
    }

    // --- asset image once revealed (draw only near end of flip-in so it doesn't look mirrored) ---
    const showAsset = (isRevealed || (isFlipping && tile.flipPhase === "in")) && tile.asset && scaleX > 0.05;
    if (showAsset) {
      const img = this.images[tile.asset!];
      if (img && img.complete && img.naturalWidth > 0) {
        const pad = tile.w * 0.18;
        const size = Math.min(tile.w, topH) - pad * 2;
        const ix = tile.x + (tile.w - size) / 2;
        const iy = topY + (topH - size) / 2;
        ctx.save();
        this.roundRect(ctx, tile.x, topY, tile.w, topH, radius);
        ctx.clip();
        ctx.drawImage(img, ix, iy, size, size);
        ctx.restore();
      }
    }

    ctx.restore();
  }

  /** Spinning arc loader drawn over a `loading` tile's darkened face. */
  private drawLoader(ctx: CanvasRenderingContext2D, tile: Tile, topY: number, topH: number): void {
    const cx = tile.x + tile.w / 2;
    const cy = topY + topH / 2;
    const radius = Math.min(tile.w, topH) * 0.22;
    const lineWidth = Math.max(2, radius * 0.22);
    const rotation = (performance.now() / 700) % (Math.PI * 2);
    const sweep = Math.PI * 1.4;

    ctx.save();
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";

    // faint full-circle track
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();

    // moving arc
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.beginPath();
    ctx.arc(cx, cy, radius, rotation, rotation + sweep);
    ctx.stroke();

    ctx.restore();
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    const radius = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
  }
}
